import "./time-slot-selection.scss";

import { AvailableSlotDto } from "@api/api";
import { useQyGetAvailableSlots } from "@factory/query/available-slots.query";
import { Button } from "primereact/button";
import { useCallback, useMemo } from "react";

interface TimeSlotSelectionProps {
  tenant: string;
  selectedDate: Date;
  selectedService: string;
  selectedProvider: string;
  selectedSlot: AvailableSlotDto | null;
  onSlotSelect: (slot: AvailableSlotDto) => void;
  onBack: () => void;
  onContinue: () => void;
}

interface SlotsByPeriod {
  morning: AvailableSlotDto[];
  afternoon: AvailableSlotDto[];
  evening: AvailableSlotDto[];
}

// API response type (camelCase from backend)
interface ApiSlot {
  startTime?: string;
  endTime?: string;
  availableSpots?: number;
  isAvailable?: boolean;
  // Also support snake_case in case API changes
  start_time?: string;
  end_time?: string;
  available_spots?: number;
  is_available?: boolean;
}

interface ApiSlotsResponse {
  date?: string;
  isOpen?: boolean;
  is_open?: boolean;
  message?: string;
  slots?: ApiSlot[];
}

// Normalize slot from API response (handles both camelCase and snake_case)
const normalizeSlot = (slot: ApiSlot): AvailableSlotDto => ({
  start_time: slot.startTime || slot.start_time || "",
  end_time: slot.endTime || slot.end_time || "",
  available_spots: slot.availableSpots ?? slot.available_spots ?? 0,
  is_available: slot.isAvailable ?? slot.is_available ?? false,
});

// Normalize the full response
const normalizeResponse = (data: ApiSlotsResponse | undefined) => {
  if (!data) return { is_open: false, slots: [], message: undefined };
  return {
    is_open: data.isOpen ?? data.is_open ?? false,
    message: data.message,
    slots: (data.slots || []).map(normalizeSlot),
  };
};

const formatTime = (timeStr: string | undefined): string => {
  if (!timeStr) return "--:--";
  const [hours, minutes] = timeStr.split(":").map(Number);
  const period = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${minutes.toString().padStart(2, "0")} ${period}`;
};

const getTimeHour = (timeStr: string | undefined): number => {
  if (!timeStr) return 0;
  return parseInt(timeStr.split(":")[0], 10);
};

const categorizeSlotsbyPeriod = (slots: AvailableSlotDto[]): SlotsByPeriod => {
  return slots.reduce(
    (acc, slot) => {
      const hour = getTimeHour(slot.start_time);
      if (hour < 12) {
        acc.morning.push(slot);
      } else if (hour < 17) {
        acc.afternoon.push(slot);
      } else {
        acc.evening.push(slot);
      }
      return acc;
    },
    { morning: [], afternoon: [], evening: [] } as SlotsByPeriod,
  );
};

export function TimeSlotSelection({
  tenant,
  selectedDate,
  selectedService,
  selectedProvider,
  selectedSlot,
  onSlotSelect,
  onBack,
  onContinue,
}: TimeSlotSelectionProps) {
  // const dateStr = selectedDate.toISOString().split("T")[0];

  // Format date in local timezone to avoid UTC conversion issues
  const year = selectedDate.getFullYear();
  const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
  const day = String(selectedDate.getDate()).padStart(2, "0");
  const dateStr = `${year}-${month}-${day}`;

  const { data: rawSlotsData, isLoading } = useQyGetAvailableSlots(
    tenant,
    dateStr,
    selectedService || "",
    selectedProvider || "",
  );

  // Normalize the API response to handle camelCase vs snake_case
  const slotsData = useMemo(
    () => normalizeResponse(rawSlotsData as unknown as ApiSlotsResponse),
    [rawSlotsData],
  );

  const slotsByPeriod = useMemo(() => {
    if (!slotsData?.slots || slotsData.slots.length === 0) {
      return { morning: [], afternoon: [], evening: [] };
    }
    return categorizeSlotsbyPeriod(slotsData.slots);
  }, [slotsData?.slots]);

  const hasAvailableSlots = useMemo(() => {
    return slotsData?.slots?.some((s) => s.is_available) ?? false;
  }, [slotsData?.slots]);

  const formatDateDisplay = useCallback((date: Date): string => {
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }, []);

  const isSlotSelected = useCallback(
    (slot: AvailableSlotDto): boolean => {
      return (
        selectedSlot?.start_time === slot.start_time &&
        selectedSlot?.end_time === slot.end_time
      );
    },
    [selectedSlot],
  );

  const renderSlotButton = (slot: AvailableSlotDto) => {
    const isSelected = isSlotSelected(slot);
    const isDisabled = !slot.is_available;

    return (
      <button
        key={slot.start_time}
        className={`time-slot ${isSelected ? "selected" : ""}`}
        onClick={() => onSlotSelect(slot)}
        disabled={isDisabled}
        type="button"
      >
        {formatTime(slot.start_time)}
        {slot.available_spots > 0 && slot.available_spots <= 3 && (
          <span className="spots-remaining">
            {slot.available_spots} spot{slot.available_spots !== 1 ? "s" : ""}{" "}
            left
          </span>
        )}
      </button>
    );
  };

  const renderPeriodSection = (
    title: string,
    icon: string,
    slots: AvailableSlotDto[],
  ) => {
    if (slots.length === 0) return null;

    const availableCount = slots.filter((s) => s.is_available).length;

    return (
      <div className="period-section">
        <div className="period-header">
          <i className={`pi ${icon}`}></i>
          <h4>{title}</h4>
          <span className="slot-count">({availableCount} available)</span>
        </div>
        <div className="slots-grid">
          {slots.map((slot) => renderSlotButton(slot))}
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="time-slot-selection">
        <div className="selection-header">
          <button className="back-btn" onClick={onBack} aria-label="Go back">
            <i className="pi pi-arrow-left"></i>
          </button>
          <div className="date-info">
            <h3>Select a Time</h3>
            <div className="date-details">
              {formatDateDisplay(selectedDate)}
            </div>
          </div>
        </div>

        <div className="loading-skeleton">
          <div className="skeleton-grid">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="skeleton-slot"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!slotsData?.is_open) {
    return (
      <div className="time-slot-selection">
        <div className="selection-header">
          <button className="back-btn" onClick={onBack} aria-label="Go back">
            <i className="pi pi-arrow-left"></i>
          </button>
          <div className="date-info">
            <h3>Select a Time</h3>
            <div className="date-details">
              {formatDateDisplay(selectedDate)}
            </div>
          </div>
        </div>

        <div className="clinic-closed">
          <i className="pi pi-times-circle"></i>
          <h4>Clinic Closed</h4>
          <p>
            {slotsData?.message ||
              "The clinic is closed on this day. Please select another date."}
          </p>
        </div>

        <div className="continue-section">
          <Button
            label="Choose Another Date"
            icon="pi pi-calendar"
            className="p-button-outlined"
            onClick={onBack}
          />
        </div>
      </div>
    );
  }

  if (!hasAvailableSlots) {
    return (
      <div className="time-slot-selection">
        <div className="selection-header">
          <button className="back-btn" onClick={onBack} aria-label="Go back">
            <i className="pi pi-arrow-left"></i>
          </button>
          <div className="date-info">
            <h3>Select a Time</h3>
            <div className="date-details">
              {formatDateDisplay(selectedDate)}
            </div>
          </div>
        </div>

        <div className="no-slots">
          <i className="pi pi-calendar-times"></i>
          <h4>Fully Booked</h4>
          <p>
            All time slots for this day have been booked. Please select another
            date.
          </p>
        </div>

        <div className="continue-section">
          <Button
            label="Choose Another Date"
            icon="pi pi-calendar"
            className="p-button-outlined"
            onClick={onBack}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="time-slot-selection">
      <div className="selection-header">
        <button className="back-btn" onClick={onBack} aria-label="Go back">
          <i className="pi pi-arrow-left"></i>
        </button>
        <div className="date-info">
          <h3>Select a Time</h3>
          <div className="date-details">{formatDateDisplay(selectedDate)}</div>
        </div>
      </div>

      <div className="slots-container">
        {renderPeriodSection("Morning", "pi-sun", slotsByPeriod.morning)}
        {renderPeriodSection("Afternoon", "pi-cloud", slotsByPeriod.afternoon)}
        {renderPeriodSection("Evening", "pi-moon", slotsByPeriod.evening)}
      </div>

      <div className="continue-section">
        {selectedSlot && (
          <div className="selected-info">
            <i className="pi pi-check-circle"></i>
            <span>
              Selected: <strong>{formatTime(selectedSlot.start_time)}</strong>
            </span>
          </div>
        )}
        <Button
          label="Continue"
          icon="pi pi-arrow-right"
          iconPos="right"
          onClick={onContinue}
          disabled={!selectedSlot}
        />
      </div>
    </div>
  );
}

export default TimeSlotSelection;
